var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from 'react';
import { Popover as HeadlessPopover } from '@headlessui/react';
import { usePopper } from 'react-popper';
import PopoverContext from './private/utils/PopoverContext';
import usePopoverContext from './private/utils/usePopoverContext';
import mergeClassnames from '../mergeClassnames/mergeClassnames';
import useRegisterChild from '../private/utils/useRegisterChild';
const PopoverRoot = ({ children, position = 'bottom', className, }) => {
    var _a;
    const [anchorEl, setAnchorEl] = React.useState();
    const [popperEl, setPopperEl] = React.useState();
    const [arrowEl, setArrowEl] = React.useState(null);
    const { state, registerChild } = useRegisterChild();
    const isArrow = (_a = state.childrens) === null || _a === void 0 ? void 0 : _a.find((name) => name === 'Arrow');
    const offset = isArrow ? 16 : 8;
    let { styles, attributes } = usePopper(anchorEl, popperEl, {
        placement: position,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, offset],
                },
            },
            { name: 'arrow', options: { element: arrowEl } },
        ],
    });
    const states = {
        popper: {
            styles: styles,
            attributes: attributes,
            setAnchor: setAnchorEl,
            setPopper: setPopperEl,
            setArrow: setArrowEl,
        },
    };
    const childrens = typeof children !== 'function' ? React.Children.toArray(children) : [];
    const callableChildren = typeof children === 'function' && children;
    return (React.createElement(PopoverContext.Provider, { value: Object.assign(Object.assign(Object.assign({}, states), state), { registerChild }) },
        React.createElement(HeadlessPopover, { className: mergeClassnames('relative', className) }, ({ open }) => (React.createElement(React.Fragment, null, typeof children === 'function'
            ? callableChildren && callableChildren({ open })
            : childrens.map((ch) => ch))))));
};
const Trigger = (_a) => {
    var { children } = _a, rest = __rest(_a, ["children"]);
    const { popper } = usePopoverContext('Popover.Trigger');
    return (React.createElement(HeadlessPopover.Button, Object.assign({ as: 'div', ref: popper === null || popper === void 0 ? void 0 : popper.setAnchor, role: "button" }, rest), children));
};
const Panel = ({ children, className, isStatic }) => {
    var _a, _b;
    const { popper, childrens: registerChildrens } = usePopoverContext('Popover.Trigger');
    const isArrow = registerChildrens === null || registerChildrens === void 0 ? void 0 : registerChildrens.find((name) => name === 'Arrow');
    const childrens = typeof children !== 'function' ? React.Children.toArray(children) : [];
    const callableChildren = typeof children === 'function' && children;
    return (React.createElement(HeadlessPopover.Panel, Object.assign({ ref: popper === null || popper === void 0 ? void 0 : popper.setPopper, style: (_a = popper === null || popper === void 0 ? void 0 : popper.styles) === null || _a === void 0 ? void 0 : _a.popper }, (_b = popper === null || popper === void 0 ? void 0 : popper.attributes) === null || _b === void 0 ? void 0 : _b.popper, { className: mergeClassnames('w-72 z-[999999] rounded-moon-s-md box-border bg-goku shadow-moon-lg overflow-y-auto', isArrow && 'shadow-none drop-shadow-[0_0_1px_rgba(0,0,0,0.4)]', 'focus:outline-none', className && className), static: isStatic }), ({ open, close }) => (React.createElement(React.Fragment, null, typeof children === 'function'
        ? callableChildren && callableChildren({ open, close })
        : childrens.map((ch) => ch)))));
};
const Arrow = ({ className }) => {
    var _a, _b;
    const { popper, registerChild } = usePopoverContext('Popover.Arrow');
    useEffect(() => {
        registerChild && registerChild('Arrow');
    }, []);
    const popperAtr = (_a = popper === null || popper === void 0 ? void 0 : popper.attributes) === null || _a === void 0 ? void 0 : _a.popper;
    const placement = popperAtr ? popperAtr['data-popper-placement'] : 'bottom';
    return (React.createElement("div", { className: mergeClassnames("[&[data-placement='top']]:-bottom-[5px] [&[data-placement='top-start']]:-bottom-[5px] [&[data-placement='top-end']]:-bottom-[5px]", "[&[data-placement='bottom']]:-top-[5px] [&[data-placement='bottom-start']]:-top-[5px] [&[data-placement='bottom-end']]:-top-[5px]", "[&[data-placement='right']]:-left-[5px] [&[data-placement='right-start']]:-left-[5px] [&[data-placement='right-end']]:-left-[5px]", "[&[data-placement='left']]:-right-[5px] [&[data-placement='left-start']]:-right-[5px] [&[data-placement='left-end']]:-right-[5px]", className), ref: popper === null || popper === void 0 ? void 0 : popper.setArrow, style: (_b = popper === null || popper === void 0 ? void 0 : popper.styles) === null || _b === void 0 ? void 0 : _b.arrow, "data-placement": placement },
        React.createElement("div", { className: "block relative rotate-45 w-3 h-3 rounded-sm bg-goku" })));
};
const Group = ({ children, className }) => (React.createElement(HeadlessPopover.Group, { className: className }, children));
const Popover = Object.assign(PopoverRoot, { Trigger, Panel, Group, Arrow });
export default Popover;
